import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApiClient } from "../api/ApiClientContext";
import { ApiError } from "../api/client";
import type {
  BudgetResponse,
  TeamSummaryEntry,
  UsageResponse,
  UsageTreesResponse,
} from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { useOnline } from "../offline/useOnline";
import { currentMonth } from "../usage/months";
import { BudgetCard } from "../usage/BudgetCard";
import { EnforcementNotice } from "../usage/EnforcementNotice";
import { LiveSeats } from "../usage/LiveSeats";
import { WhoIsOn } from "../usage/WhoIsOn";
import { PeakCalendar } from "../usage/PeakCalendar";
import { TeamDailyTable } from "../usage/TeamDailyTable";
import { TopSpenders } from "../usage/TopSpenders";
import { MonthPicker } from "../usage/MonthPicker";
import { TreePicker } from "../usage/TreePicker";

type State =
  | { kind: "loading" }
  | { kind: "ok"; data: UsageResponse }
  | { kind: "leaf" }
  | { kind: "error"; message: string };

/** The admin-only client list. A union rather than a nullable list because
 *  "still coming" and "asked and failed" send the page different ways: the
 *  second one settles it, and an admin with no `?team=` has nothing left to
 *  wait for. */
type Roots =
  | { kind: "loading" }
  | { kind: "ok"; list: TeamSummaryEntry[] }
  | { kind: "error"; message: string };

/** Admin-only budget/spenders fetches. A loading state a month switch can
 *  synchronously return to, so the page never shows one month's seats next
 *  to a different month's spend. */
type BudgetState =
  | { kind: "loading" }
  | { kind: "ok"; data: BudgetResponse }
  | { kind: "error"; message: string };

type TreesState =
  | { kind: "loading" }
  | { kind: "ok"; data: UsageTreesResponse }
  | { kind: "error"; message: string };

/** Spec §9. Admin picks a client tree; a parent sees its own; a leaf sees one line. */
export function UsagePage(): JSX.Element {
  const client = useApiClient();
  const auth = useAuth();
  const online = useOnline();
  const isAdmin = auth.team?.role === "admin";
  const [params, setParams] = useSearchParams();
  const month = params.get("month") ?? currentMonth();
  const team = params.get("team");
  const [roots, setRoots] = useState<Roots>({ kind: "loading" });
  const [state, setState] = useState<State>({ kind: "loading" });
  const [budget, setBudget] = useState<BudgetState>({ kind: "loading" });
  const [trees, setTrees] = useState<TreesState>({ kind: "loading" });
  // Only an admin ever fetches roots, so these are false for everyone else.
  const rootsError = roots.kind === "error" ? roots.message : null;
  const noClients = isAdmin && roots.kind === "ok" && roots.list.length === 0;
  // Nothing is on its way: either there are no clients to show, or the list
  // failed and no `?team=` named one anyway. Either way, don't say "Loading…".
  const stalled = noClients || (isAdmin && team === null && rootsError !== null);

  // Admin: the root teams, once. Deliberately not keyed on the query, so
  // changing month or client never re-asks — and its failure lands in its own
  // state, where it cannot clobber a usage page that already loaded.
  useEffect(() => {
    if (!isAdmin || !online) return undefined;
    let cancelled = false;
    void client
      .listTeams()
      .then((r) => {
        if (cancelled) return;
        const list = r.teams.filter((t) => t.parent === null || t.parent === undefined);
        setRoots({ kind: "ok", list });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setRoots({ kind: "error", message: err instanceof Error ? err.message : String(err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client, isAdmin, online]);

  // Point the URL at the first client once the list is in.
  useEffect(() => {
    if (roots.kind !== "ok" || team !== null) return;
    const first = roots.list[0];
    if (first === undefined) return;
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        next.set("team", first.slug);
        return next;
      },
      { replace: true },
    );
  }, [roots, team, setParams]);

  // Usage is fetched off `team` alone — never off `roots`, or a deep link that
  // already names a team would fetch once at mount and again when the client
  // list lands. An empty list simply never sets `team`, so nothing is fetched.
  useEffect(() => {
    if (!online) return undefined;
    if (isAdmin && team === null) return undefined;
    let cancelled = false;
    setState({ kind: "loading" });
    void client
      .getUsage(isAdmin ? { month, team: team as string } : { month })
      .then((data) => {
        if (!cancelled) setState({ kind: "ok", data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.code === "not_a_parent") setState({ kind: "leaf" });
        else setState({ kind: "error", message: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [client, isAdmin, month, team, online]);

  // Admin only: the budget card and every tree's spenders. Deliberately keyed
  // on [client, isAdmin, month, online] alone — never on `team` or `roots` —
  // so picking a different client tree, or the roots list landing, never
  // re-asks for either. Both reset to "loading" synchronously, before either
  // fetch starts, so a month switch never shows the new month's seats next
  // to the previous month's spend or spenders while the new answer is still
  // in flight.
  useEffect(() => {
    if (!isAdmin || !online) return undefined;
    let cancelled = false;
    setBudget({ kind: "loading" });
    setTrees({ kind: "loading" });
    void client
      .getBudget({ month })
      .then((data) => {
        if (!cancelled) setBudget({ kind: "ok", data });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setBudget({ kind: "error", message: err instanceof Error ? err.message : String(err) });
        }
      });
    void client
      .getUsageTrees({ month })
      .then((data) => {
        if (!cancelled) setTrees({ kind: "ok", data });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setTrees({ kind: "error", message: err instanceof Error ? err.message : String(err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client, isAdmin, month, online]);

  const setMonth = (m: string): void =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      next.set("month", m);
      return next;
    });
  const setTeam = (slug: string): void =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      next.set("team", slug);
      return next;
    });

  return (
    <div style={{ padding: "16px 24px", maxWidth: 1100 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, margin: 0, marginRight: "auto" }}>Usage</h1>
        {isAdmin && roots.kind === "ok" && roots.list.length > 0 && (
          <TreePicker
            roots={roots.list}
            value={team ?? roots.list[0]!.slug}
            onChange={setTeam}
          />
        )}
        <MonthPicker month={month} onChange={setMonth} />
      </div>
      {!online && <p>Usage needs a connection.</p>}
      {online && rootsError !== null && (
        <p role="alert">Couldn't load the client list: {rootsError}</p>
      )}
      {online && noClients && <p>No client teams yet.</p>}
      {online && !stalled && state.kind === "loading" && <p>Loading…</p>}
      {online && state.kind === "leaf" && <p>Nothing to show for your team.</p>}
      {online && state.kind === "error" && <p role="alert">Couldn't load usage: {state.message}</p>}
      {online && state.kind === "ok" && (
        <>
          <EnforcementNotice enforcement={state.data.enforcement} />
          <LiveSeats live={state.data.live} included={state.data.included} />
          <WhoIsOn teams={state.data.live.teams} />
          <PeakCalendar
            month={state.data.month.month}
            days={state.data.month.days}
            included={state.data.included}
          />
          <TeamDailyTable
            days={state.data.month.days}
            teams={state.data.live.teams}
            included={state.data.included}
          />
          {isAdmin && (
            <BudgetCard
              budget={budget.kind === "ok" ? budget.data : null}
              error={budget.kind === "error" ? budget.message : null}
            />
          )}
          {isAdmin ? (
            trees.kind === "error" ? (
              <p role="alert">Couldn't load the spenders: {trees.message}</p>
            ) : trees.kind === "loading" ? (
              <section style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 18, margin: "0 0 8px" }}>Who spent the most</h2>
                <p style={{ margin: 0 }}>Loading…</p>
              </section>
            ) : (
              <TopSpenders
                trees={trees.data.trees}
                people={null}
                failed={trees.data.failed}
              />
            )
          ) : (
            <TopSpenders trees={null} people={state.data.people} />
          )}
        </>
      )}
    </div>
  );
}
